/**
 * Other data: `DV_MULTIMEDIA`, `DV_PARSABLE`, and `DV_STATE`.
 *
 * Reviewer coverage in this category is **uneven** — several of these types were
 * never reviewed from the FHIR side — so rows the specifications do not clearly
 * support are recorded `open` rather than asserted. See
 * [Open Items](open-items.html) for the coverage table itself.
 */

import type { Cite, Mapping, Review } from '../src/model/types.ts';

const RM = 'https://specifications.openehr.org/releases/RM/latest/data_types.html';
const TERM = 'https://specifications.openehr.org/releases/TERM/latest/SupportTerminology.html';
const R5 = 'https://hl7.org/fhir/R5/datatypes.html';
const EXT_PACK = 'https://hl7.org/fhir/extensions/StructureDefinition';

function rm(anchor: string, label: string): Cite {
  return { url: `${RM}#${anchor}`, label, verification: 'spec-local' };
}

function r5(anchor: string, label: string): Cite {
  return { url: `${R5}#${anchor}`, label, verification: 'spec-local' };
}

function ext(name: string, label: string): Cite {
  return { url: `${EXT_PACK}-${name}.html`, label, verification: 'extension-unverified' };
}

const DV_MULTIMEDIA = rm('_dv_multimedia_class', 'openEHR RM — DV_MULTIMEDIA');
const DV_PARSABLE = rm('_dv_parsable_class', 'openEHR RM — DV_PARSABLE');
const DV_STATE = rm('_dv_state_class', 'openEHR RM — DV_STATE');
const DV_ENCAPSULATED = rm('_dv_encapsulated_class', 'openEHR RM — DV_ENCAPSULATED');
const OPENEHR_LANGUAGES: Cite = {
  url: `${TERM}#_languages`,
  label: 'openEHR TERM — languages code set (External_id: ISO_639-1)',
  verification: 'spec-local',
};
const ATTACHMENT = r5('Attachment', 'FHIR R5 — Attachment');
const FHIR_STRING = r5('string', 'FHIR R5 — string');
const CODEABLE_CONCEPT = r5('CodeableConcept', 'FHIR R5 — CodeableConcept');

const NOT_REVIEWED: Review = { openehr: [], fhir: [] };
const REVIEWED_OPENEHR_ONLY: Review = { openehr: ['Severin'], fhir: [] };

/**
 * The mandatory-attribute rule, cross-referenced from every row it governs.
 * Appended to a row note rather than restated, so the wording cannot drift.
 */
const MANDATORY_RULE =
  ' The openEHR attribute is **mandatory** and the FHIR element is optional, so an ' +
  'incoming instance that omits it cannot be converted: the reference implementation ' +
  'produces **nothing** rather than inventing a value. See ' +
  '[the mandatory-attribute rule](conventions.html#mandatory-attributes).';

/**
 * The five `Attachment` elements beyond `creation` that no `DV_MULTIMEDIA`
 * attribute can receive. One row each, because the page prose promises a
 * per-element inventory; the reason and the owner are the same for all five.
 */
const MEDIA_DETAIL_ROWS = (
  [
    ['height', 'positiveInt', 'the height of a still image or video, in pixels'],
    ['width', 'positiveInt', 'the width of a still image or video, in pixels'],
    ['frames', 'positiveInt', 'the number of frames in a multi-frame image'],
    ['duration', 'decimal', 'the running length of an audio or video attachment, in seconds'],
    ['pages', 'positiveInt', 'the number of pages in a paginated document'],
  ] as const
).map(([element, type, what]) => ({
  id: `fhir:attachment.${element}`,
  scope: 'datatype' as const,
  openehr: {
    kind: 'none' as const,
    reason:
      `The published openEHR *Media File* CLUSTER archetype has no \`${element}\` ` +
      'element, and neither does `DV_MULTIMEDIA`. A draft ' +
      '`CLUSTER.extended_media_details` archetype provides one.',
    cite: DV_MULTIMEDIA,
  },
  fhir: [
    {
      path: `Attachment.${element}`,
      cardinality: '0..1',
      type,
      kind: 'element' as const,
      cite: ATTACHMENT,
    },
  ] as const,
  toFhir: {
    fidelity: 'unmapped' as const,
    reason: 'No `DV_MULTIMEDIA` attribute produces it.',
    owner: 'openehr-modelling' as const,
  },
  toOpenehr: {
    fidelity: 'unmapped' as const,
    reason:
      `Carried by an **extension archetype**, not by the data type: ${what} has no ` +
      'openEHR data-type home. Authoring the archetype belongs to the openEHR modelling ' +
      'team.',
    owner: 'openehr-modelling' as const,
  },
  maturity: 'open' as const,
}));

const dvMultimediaToAttachment = {
  id: 'dv-multimedia-to-attachment',
  category: 'other',
  openehrType: 'DV_MULTIMEDIA',
  fhirType: 'Attachment',
  title: 'DV_MULTIMEDIA ↔ Attachment',
  scope: 'datatype',
  sources: [DV_MULTIMEDIA, ATTACHMENT, DV_ENCAPSULATED, OPENEHR_LANGUAGES],
  review: NOT_REVIEWED,
  rows: [
    {
      id: 'dv-multimedia.data',
      scope: 'datatype',
      openehr: {
        path: 'DV_MULTIMEDIA.data',
        cardinality: '0..1',
        type: 'List<Octet>',
        kind: 'element',
        cite: DV_MULTIMEDIA,
      },
      fhir: [
        {
          path: 'Attachment.data',
          cardinality: '0..1',
          type: 'base64Binary',
          kind: 'element',
          cite: ATTACHMENT,
        },
      ],
      toFhir: { fidelity: 'lossless' },
      toOpenehr: { fidelity: 'lossless' },
      maturity: 'open',
    },
    {
      id: 'dv-multimedia.uri',
      scope: 'datatype',
      openehr: {
        path: 'DV_MULTIMEDIA.uri',
        cardinality: '0..1',
        type: 'DV_URI',
        kind: 'element',
        cite: DV_MULTIMEDIA,
      },
      fhir: [
        {
          path: 'Attachment.url',
          cardinality: '0..1',
          type: 'url',
          kind: 'element',
          cite: ATTACHMENT,
        },
      ],
      toFhir: { fidelity: 'lossless' },
      toOpenehr: { fidelity: 'lossless' },
      maturity: 'open',
    },
    {
      id: 'dv-multimedia.media_type',
      scope: 'datatype',
      openehr: {
        path: 'DV_MULTIMEDIA.media_type',
        cardinality: '1..1',
        type: 'CODE_PHRASE',
        kind: 'element',
        cite: DV_MULTIMEDIA,
      },
      fhir: [
        {
          path: 'Attachment.contentType',
          cardinality: '0..1',
          type: 'code',
          kind: 'element',
          cite: ATTACHMENT,
        },
      ],
      toFhir: { fidelity: 'lossless' },
      toOpenehr: { fidelity: 'lossless' },
      maturity: 'open',
      note:
        'An IANA MIME type on both sides. Where an openEHR `charset` applies to the ' +
        'attachment data itself it SHOULD be folded into the MIME type as a `charset` ' +
        'parameter; where it applies to local content the content is converted to UTF-8.' +
        MANDATORY_RULE,
    },
    {
      id: 'dv-multimedia.size',
      scope: 'datatype',
      openehr: {
        path: 'DV_MULTIMEDIA.size',
        cardinality: '1..1',
        type: 'Integer',
        kind: 'element',
        cite: DV_MULTIMEDIA,
      },
      fhir: [
        {
          path: 'Attachment.size',
          cardinality: '0..1',
          type: 'integer64',
          kind: 'element',
          cite: ATTACHMENT,
        },
      ],
      toFhir: { fidelity: 'lossless' },
      toOpenehr: { fidelity: 'lossless' },
      maturity: 'open',
      note:
        'The **original** size in bytes, before any compression or encoding. `0` is a real ' +
        'size, not a missing one. The two types are **not the same width**: RM `Integer` ' +
        'is 32-bit and R5 `Attachment.size` is an `integer64`, so an attachment larger ' +
        'than 2,147,483,647 bytes has no `DV_MULTIMEDIA.size` to land in. That sub-case is ' +
        'the row below, and it is the same gap the numeric category publishes as ' +
        '`Integer64[overflow]` on [Numeric Primitives](mapping-numeric.html). The two ' +
        'sides also differ **on the wire**: an `integer64` is serialised as a **JSON ' +
        'String**, so a conformant `Attachment.size` for 20,416 bytes is `"20416"`, while ' +
        '`DV_MULTIMEDIA.size` is the JSON number `20416` — the same reasoning the ' +
        '`Integer64` row on [Numeric Primitives](mapping-numeric.html) sets out.' +
        MANDATORY_RULE,
    },
    {
      id: 'fhir:attachment.size.overflow',
      scope: 'datatype',
      openehr: {
        kind: 'none',
        reason:
          '`DV_MULTIMEDIA.size` is an RM `Integer`, which is 32-bit. There is no openEHR ' +
          'attribute that can hold a size beyond \u00b12,147,483,647, so an attachment ' +
          'larger than about 2 GiB has nowhere to record its size.',
        cite: DV_MULTIMEDIA,
      },
      fhir: [
        {
          path: 'Attachment.size[overflow]',
          cardinality: '0..1',
          type: 'integer64',
          kind: 'element',
          cite: ATTACHMENT,
        },
      ],
      toFhir: {
        fidelity: 'unmapped',
        reason:
          'openEHR cannot produce a value outside its own 32-bit range, so this case never ' +
          'arises in this direction.',
        owner: 'openehr-modelling',
      },
      toOpenehr: {
        fidelity: 'unmapped',
        reason:
          'Nothing in openEHR can receive it, and `size` is mandatory, so no ' +
          '`DV_MULTIMEDIA` is produced at all rather than one carrying a truncated or ' +
          'invented size. Widening `DV_MULTIMEDIA.size` is an openEHR Reference Model ' +
          'question.',
        owner: 'openehr-modelling',
      },
      maturity: 'open',
      note:
        'Cross-refer to `Integer64[overflow]` on ' +
        '[Numeric Primitives](mapping-numeric.html): it is the identical 32-bit/64-bit gap, ' +
        'and publishing one as a first-class `unmapped` row while folding the other into a ' +
        '`lossless` claim was a transcription slip rather than a position. The overflowing ' +
        'value is a **JSON String** like every other `integer64`, so the boundary case ' +
        'reads `"4294967296"`, not `4294967296`.',
    },
    {
      id: 'dv-multimedia.alternate_text',
      scope: 'datatype',
      openehr: {
        path: 'DV_MULTIMEDIA.alternate_text',
        cardinality: '0..1',
        type: 'String',
        kind: 'element',
        cite: DV_MULTIMEDIA,
      },
      fhir: [
        {
          path: 'Attachment.title',
          cardinality: '0..1',
          type: 'string',
          kind: 'element',
          cite: ATTACHMENT,
        },
      ],
      toFhir: { fidelity: 'lossless' },
      toOpenehr: { fidelity: 'lossless' },
      maturity: 'open',
      note: 'Display text to show in place of the multimedia content.',
    },
    {
      id: 'dv-multimedia.integrity_check',
      scope: 'datatype',
      openehr: {
        path: 'DV_MULTIMEDIA.integrity_check',
        cardinality: '0..1',
        type: 'List<Octet>',
        kind: 'element',
        cite: DV_MULTIMEDIA,
      },
      fhir: [
        {
          path: 'Attachment.hash',
          cardinality: '0..1',
          type: 'base64Binary',
          kind: 'element',
          cite: ATTACHMENT,
        },
      ],
      toFhir: { fidelity: 'lossless' },
      toOpenehr: { fidelity: 'lossless' },
      maturity: 'open',
      note:
        '`Attachment.hash` is **defined as SHA-1**. A hash computed with any other ' +
        'algorithm is carried only alongside `integrity_check_algorithm` — see the next ' +
        'row.',
    },
    {
      id: 'dv-multimedia.integrity_check_algorithm',
      scope: 'datatype',
      openehr: {
        path: 'DV_MULTIMEDIA.integrity_check_algorithm',
        cardinality: '0..1',
        type: 'CODE_PHRASE',
        kind: 'element',
        cite: DV_MULTIMEDIA,
      },
      fhir: {
        kind: 'none',
        reason:
          '`Attachment.hash` is SHA-1 by definition and R5 has no element naming a ' +
          'different algorithm.',
        cite: ATTACHMENT,
      },
      toFhir: {
        fidelity: 'unmapped',
        reason:
          'openEHR permits SHA-1, SHA-224, SHA-256, SHA-384, SHA-512, SHA-512/224, and ' +
          'SHA-512/256; only SHA-1 has a home in R5. FHIR-55422 is **resolved** and adds a ' +
          'core `alternate-hash` extension keeping SHA-1 as the default, but it has not ' +
          'been published yet. **No extension is invented here.**',
        owner: 'FHIR-55422',
      },
      toOpenehr: {
        fidelity: 'unmapped',
        reason:
          'Nothing arrives; a receiving system MAY assume SHA-1, which is an inference ' +
          'from the FHIR definition rather than a carried value.',
        owner: 'FHIR-55422',
      },
      maturity: 'open',
    },
    {
      id: 'dv-multimedia.compression_algorithm',
      scope: 'datatype',
      openehr: {
        path: 'DV_MULTIMEDIA.compression_algorithm',
        cardinality: '0..1',
        type: 'CODE_PHRASE',
        kind: 'element',
        cite: DV_MULTIMEDIA,
      },
      fhir: {
        kind: 'none',
        reason: 'FHIR `Attachment` has no element naming a compression algorithm.',
        cite: ATTACHMENT,
      },
      toFhir: {
        fidelity: 'unmapped',
        reason:
          'openEHR permits `compress`, `deflate`, `gzip`, `zlib`, and `other`; FHIR has no ' +
          'equivalent. FHIR-56003 requests a `content-encoding` extension and is triaged. ' +
          'The pragmatic alternative is to **decompress during conversion**, which removes ' +
          'the need for the field. **No extension is invented here.**',
        owner: 'FHIR-56003',
      },
      toOpenehr: {
        fidelity: 'unmapped',
        reason: 'Nothing arrives, because nothing was emitted.',
        owner: 'FHIR-56003',
      },
      maturity: 'open',
    },
    {
      id: 'dv-multimedia.thumbnail',
      scope: 'datatype',
      openehr: {
        path: 'DV_MULTIMEDIA.thumbnail',
        cardinality: '0..1',
        type: 'DV_MULTIMEDIA',
        kind: 'element',
        cite: DV_MULTIMEDIA,
      },
      fhir: {
        kind: 'none',
        reason:
          'The existing `documentreference-thumbnail` extension is a flag on ' +
          '`DocumentReference.content` saying that the content *is* a thumbnail; it does ' +
          'not attach a thumbnail to an `Attachment`.',
        cite: ATTACHMENT,
      },
      toFhir: {
        fidelity: 'unmapped',
        reason:
          'A nested thumbnail has no FHIR home. A new extension on `Attachment` has been ' +
          'requested as FHIR-56002 and is **not yet published**. **No extension is invented ' +
          'here.**',
        owner: 'FHIR-56002',
      },
      toOpenehr: {
        fidelity: 'unmapped',
        reason: 'Nothing arrives, because nothing was emitted.',
        owner: 'FHIR-56002',
      },
      maturity: 'open',
    },
    {
      id: 'fhir:attachment.creation',
      scope: 'datatype',
      openehr: {
        kind: 'none',
        reason:
          'The published openEHR *Media File* CLUSTER archetype has no `creation` ' +
          'element. A draft `CLUSTER.extended_media_details` archetype provides one.',
        cite: DV_MULTIMEDIA,
      },
      fhir: [
        {
          path: 'Attachment.creation',
          cardinality: '0..1',
          type: 'dateTime',
          kind: 'element',
          cite: ATTACHMENT,
        },
      ],
      toFhir: {
        fidelity: 'unmapped',
        reason: 'No `DV_MULTIMEDIA` attribute produces it.',
        owner: 'openehr-modelling',
      },
      toOpenehr: {
        fidelity: 'unmapped',
        reason:
          'Carried by an **extension archetype**, not by the data type. This guide links ' +
          'the archetype and describes it; authoring it belongs to the openEHR modelling ' +
          'team, who have an open proposal to fold the extended elements into the published ' +
          '*Media File* CLUSTER.',
        owner: 'openehr-modelling',
      },
      maturity: 'open',
      note:
        'One row per element, because the page prose promises a per-element inventory and ' +
        'a reader looks for one. `openehr-modelling` is a **team, not a ticket**: the ' +
        'proposal to fold these elements into the published *Media File* CLUSTER is ' +
        'tracked on [Open Items](open-items.html) and has no Jira number. The *Media File* ' +
        'CLUSTER archetype, rather than a bare `DV_MULTIMEDIA`, is the closer target for a ' +
        'FHIR `Attachment`.',
    },
    ...MEDIA_DETAIL_ROWS,
    {
      id: 'dv-encapsulated.language',
      scope: 'datatype',
      openehr: {
        path: 'DV_ENCAPSULATED.language',
        cardinality: '0..1',
        type: 'CODE_PHRASE',
        kind: 'element',
        cite: DV_ENCAPSULATED,
      },
      fhir: [
        {
          path: 'Attachment.language',
          cardinality: '0..1',
          type: 'code',
          kind: 'element',
          cite: ATTACHMENT,
        },
      ],
      toFhir: {
        fidelity: 'lossy',
        drops: [
          {
            path: 'DV_ENCAPSULATED.language.terminology_id',
            reason:
              '`Attachment.language` is a `code` **required**-bound to `all-languages`, so ' +
              'it carries the tag alone; the `CODE_PHRASE.terminology_id` that stated ' +
              'which scheme the tag belongs to has no home on a bare `code`',
          },
        ],
      },
      toOpenehr: {
        fidelity: 'lossy',
        drops: [
          {
            path: 'Attachment.language[region-subtag]',
            reason:
              '`Attachment.language`\u2019s `all-languages` binding is **BCP 47** and ' +
              'openEHR binds `language` to its own published `ISO_639-1` code set. BCP 47 ' +
              'strictly contains ISO 639-1, so `en-US` and `zh-Hant` are BCP 47 tags that ' +
              'ISO 639-1 has no code for. The primary language subtag is carried and the ' +
              'region or script subtag is not',
          },
          {
            path: 'Attachment.language[outside-code-set]',
            reason:
              'a tag that is not an ISO 639-1 alpha-2 code is outside the code set ' +
              '`DV_ENCAPSULATED.Language_valid` requires, so **no `language` is written at ' +
              'all** rather than a `terminology_id` being invented for a code the code set ' +
              'does not hold. `language` is `0..1`, and the attachment itself converts ' +
              'faithfully',
          },
        ],
      },
      maturity: 'open',
      note:
        '`DV_MULTIMEDIA` inherits `language` from `DV_ENCAPSULATED` (RM § 9.2.1), so — ' +
        'unlike `creation`, `height`, `width`, `frames`, `duration`, and `pages` — this ' +
        'one **does** have an openEHR counterpart, and folding it into a ' +
        'no-counterpart claim was a fabricated gap. Coming back, the two standards do not ' +
        'bind language to the same set: FHIR\u2019s binding is **BCP 47** and openEHR ' +
        'publishes exactly one language code set, ' +
        '`Id: languages, External_id: ISO_639-1`, which ' +
        '`DV_ENCAPSULATED.Language_valid` requires the value to belong to. BCP 47 ' +
        '**strictly contains** ISO 639-1, so the inbound conversion is a narrowing and ' +
        'says so, rather than deriving an openEHR identifier from a FHIR binding.',
    },
    {
      id: 'dv-multimedia.charset',
      scope: 'datatype',
      openehr: {
        path: 'DV_MULTIMEDIA.charset',
        cardinality: '0..1',
        type: 'CODE_PHRASE',
        kind: 'element',
        cite: DV_ENCAPSULATED,
      },
      fhir: {
        kind: 'none',
        reason:
          'FHIR mandates UTF-8 and has no element recording a character set on an ' +
          '`Attachment`; where the charset applies to the attached bytes it belongs in the ' +
          '`contentType` MIME parameter instead.',
        cite: ATTACHMENT,
      },
      toFhir: {
        fidelity: 'unmapped',
        reason:
          'Not carried as a field. Where the charset applies to the attachment data it is ' +
          'folded into `contentType`; where it applies to local content the content is ' +
          'converted to UTF-8.',
      },
      toOpenehr: {
        fidelity: 'unmapped',
        reason: 'Nothing arrives as a distinct field.',
      },
      maturity: 'settled',
      note:
        'The `DV_ENCAPSULATED` parent also brings `language`. Note that the openEHR **Base ' +
        'IG** models `DV_ENCAPSULATED` differently from the Reference Model; **the ' +
        'Reference Model is the authority** cited here.',
    },
  ],
} satisfies Mapping;

const dvParsableToString = {
  id: 'dv-parsable-to-string',
  category: 'other',
  openehrType: 'DV_PARSABLE',
  fhirType: 'string',
  title: 'DV_PARSABLE ↔ string',
  scope: 'datatype',
  sources: [DV_PARSABLE, FHIR_STRING, DV_ENCAPSULATED],
  review: NOT_REVIEWED,
  rows: [
    {
      id: 'dv-parsable.value',
      scope: 'datatype',
      openehr: {
        path: 'DV_PARSABLE.value',
        cardinality: '1..1',
        type: 'String',
        kind: 'element',
        cite: DV_PARSABLE,
      },
      fhir: [
        {
          path: 'string.value',
          cardinality: '0..1',
          type: 'string',
          kind: 'element',
          cite: FHIR_STRING,
        },
      ],
      toFhir: { fidelity: 'lossless' },
      toOpenehr: {
        fidelity: 'unmapped',
        reason:
          'A FHIR `string` states nothing about the syntax its value is written in, and ' +
          '`DV_PARSABLE.formalism` is mandatory, so **no `DV_PARSABLE` can be produced ' +
          'from a `string` alone**. The formalism has to come from the element definition, ' +
          'which a data-type conversion never sees. This mapping is one-directional.',
        owner: 'session:dv-parsable-formalism',
      },
      maturity: 'open',
      note:
        'Where the formalism is markdown, a `markdown` element is the better target. For ' +
        'genomic content such as HGVS, the Genomics Reporting IG uses a ' +
        '`CodeableConcept.text` pattern instead.',
    },
    {
      id: 'dv-parsable.formalism',
      scope: 'datatype',
      openehr: {
        path: 'DV_PARSABLE.formalism',
        cardinality: '1..1',
        type: 'String',
        kind: 'element',
        cite: DV_PARSABLE,
      },
      fhir: {
        kind: 'none',
        reason:
          'No FHIR element or extension carries the syntax a parsable instance is written ' +
          'in. The `mimeType` extension is **not** it: its declared context is ' +
          '`Questionnaire.item` and `ElementDefinition`, not `string` or any data type, ' +
          'and its purpose is a design-time constraint on the attachments an element ' +
          'permits — not a statement about an instance.',
        cite: FHIR_STRING,
      },
      toFhir: {
        fidelity: 'unmapped',
        reason:
          'Nothing carries it. The working group recorded a **Decision made** to use the ' +
          '`mimeType` extension; the extension definition does not support that decision, ' +
          'and this guide publishes the gap rather than an instance no validator accepts.',
        owner: 'session:dv-parsable-formalism',
      },
      toOpenehr: {
        fidelity: 'unmapped',
        reason: 'Nothing arrives to map back, because nothing was emitted.',
        owner: 'session:dv-parsable-formalism',
      },
      maturity: 'open',
      note:
        '`formalism` is **mandatory** in openEHR, so this gap is what makes ' +
        '`DV_PARSABLE ↔ string` a one-way mapping: the value travels to FHIR, and nothing ' +
        'travels back. Inventing a FHIR usage to fill an openEHR gap is exactly what this ' +
        'guide will not do, so no substitute extension is proposed here — the decision ' +
        'belongs to the working group, and it is recorded as open.',
    },
  ],
} satisfies Mapping;

const dvStateToCodeableConcept = {
  id: 'dv-state-to-codeable-concept',
  category: 'other',
  openehrType: 'DV_STATE',
  fhirType: 'CodeableConcept',
  title: 'DV_STATE ↔ CodeableConcept',
  scope: 'datatype',
  sources: [DV_STATE, CODEABLE_CONCEPT],
  review: REVIEWED_OPENEHR_ONLY,
  rows: [
    {
      id: 'dv-state.value',
      scope: 'datatype',
      openehr: {
        path: 'DV_STATE.value',
        cardinality: '1..1',
        type: 'DV_CODED_TEXT',
        kind: 'element',
        cite: DV_STATE,
      },
      fhir: [
        {
          path: 'CodeableConcept',
          cardinality: '0..1',
          kind: 'element',
          cite: CODEABLE_CONCEPT,
        },
      ],
      toFhir: { fidelity: 'lossless' },
      toOpenehr: {
        fidelity: 'lossy',
        drops: [
          {
            path: 'Coding.version',
            reason:
              'openEHR has no separate version field and no format for combining system ' +
              'and version into `terminology_id` has been chosen, so the version is not ' +
              'carried — this conversion delegates to `DV_CODED_TEXT ↔ CodeableConcept`, ' +
              'and through it to `CODE_PHRASE ↔ Coding`, and carries both mappings\u2019 ' +
              'drops forward',
          },
          {
            path: 'CodeableConcept.coding.userSelected',
            reason:
              'openEHR has no field marking which coding a user chose; the flag is ' +
              'consumed to select the inner `defining_code` and is not carried any further',
          },
        ],
      },
      delegates: ['dv-coded-text-to-codeable-concept'],
      maturity: 'open',
      note:
        'The state **name** maps as an ordinary `DV_CODED_TEXT`; see ' +
        '[Coded Data](mapping-coded.html). What does **not** map is the state machine it ' +
        'belongs to.' + MANDATORY_RULE,
    },
    {
      id: 'dv-state.is_terminal',
      scope: 'datatype',
      openehr: {
        path: 'DV_STATE.is_terminal',
        cardinality: '1..1',
        type: 'Boolean',
        kind: 'element',
        cite: DV_STATE,
      },
      fhir: {
        kind: 'none',
        reason:
          'FHIR has **no data type for a state-machine value**. It models workflow state as ' +
          'resource-level elements such as `Task.status`, each with its own state machine, ' +
          'and none of them carries a terminal flag on the value.',
        cite: CODEABLE_CONCEPT,
      },
      toFhir: {
        fidelity: 'unmapped',
        reason:
          'There is no FHIR element for it and **no extension is invented here**. Carrying ' +
          'it would require either an extension or handling at the archetype and resource ' +
          'level.',
        owner: 'working-group',
      },
      toOpenehr: {
        fidelity: 'unmapped',
        reason:
          '`is_terminal` is mandatory in openEHR, so a mapping engine must infer it from ' +
          'the state machine the archetype defines — an inference, not a carried value.',
        owner: 'working-group',
      },
      maturity: 'open',
      note:
        '`DV_STATE` sees very little use. One participant has never seen it used at all ' +
        'and suggests it is legacy, superseded by `ISM_TRANSITION`. This row is one of the ' +
        'two **recorded exceptions** to ' +
        '[the mandatory-attribute rule](conventions.html#mandatory-attributes): nothing in ' +
        'a `CodeableConcept` can source `is_terminal`, the openEHR-only gap is published ' +
        'here, and the reference implementation infers `false` rather than refusing the ' +
        'whole conversion. `contract.test.ts` pins the exception, so the list cannot grow ' +
        'unnoticed.',
    },
  ],
} satisfies Mapping;

export default [
  dvMultimediaToAttachment,
  dvParsableToString,
  dvStateToCodeableConcept,
] satisfies readonly Mapping[];
